// @ts-check
import { Entity } from "./model";
import { Component } from "../engine";
import { ZERO, Vector2D, mirrorPointOnLine } from "../engine/vector";
import { PLAYER_1, PLAYER_2, INSIDE, OUTSIDE, EVENT_INGAME_PLAYER_HITS_BALL, EVENT_INGAME_BALL_HITS_NET, EVENT_INGAME_BALL_HITS_GROUND, LOGIC_GAME_INTRO, LOGIC_GAME_KICKOFF, EVENT_INGAME_PLAYER_HITS_GROUND, LOGIC_GAME_RUNNING } from "../global";

/**
 * Game Physics
 */
export class GamePhysics extends Component {
    constructor() {
        super();
        this.move = new Vector2D();
        this.posD = new Vector2D();
        this.posS = new Vector2D();
        this.diff = new Vector2D();
        this.result = new Vector2D();
        this.forceD = new Vector2D();
        this.forceS = new Vector2D();
        this.netTip = new Entity();
    }

    init() {
        // Create net tip entity
        this.netTip.position = new Vector2D(0, this.state.netTipY);
        this.netTip.radius = this.state.netTipRadius;
    }

    update() {
        // Init state
        var state = this.state;

        // Do not do anything if cancel menu is shown
        if (state.paused || state.logic == LOGIC_GAME_INTRO) {
            return;
        }

        // Update scene
        this.updatePlayer(state, state.player1);
        this.updatePlayer(state, state.player2);
        this.updateBall(state, state.ball);
        this.updateCamera(state);
    }

    /**
     * @param player
     */
    updatePlayer(state, player) {
        this.move.set(player.position);
        this.moveEntity(state, player);
        this.collideWall(player, state.wall, INSIDE, false);
        this.collideWall(player, state.netTipRadius, OUTSIDE, false);
        var ground = this.collideGround(state, player, false);

        player.motion.set(this.move.sub(player.position).scale(-1));
        if (ground && Math.abs(player.motion.y) > 0.1) {
            this.engine.fireEvent(EVENT_INGAME_PLAYER_HITS_GROUND);
        }
    }

    /**
     * @param ball
     */
    updateBall(state, ball) {
        this.move.set(ball.position);
        if (state.logic != LOGIC_GAME_KICKOFF && !ball.sticky) {
            this.moveEntity(state, ball);
        }

        if (state.logic == LOGIC_GAME_KICKOFF || state.logic == LOGIC_GAME_RUNNING) {
            if (this.collideEntities(ball, state.player1)) {
                state.lastContact = PLAYER_1;
                this.engine.fireEvent(EVENT_INGAME_PLAYER_HITS_BALL);
            }

            if (this.collideEntities(ball, state.player2)) {
                state.lastContact = PLAYER_2;
                this.engine.fireEvent(EVENT_INGAME_PLAYER_HITS_BALL);
            }
        }

        this.collideWall(ball, state.wall, INSIDE, true);
        if (this.collideBallNet(ball)) {
            this.engine.fireEvent(EVENT_INGAME_BALL_HITS_NET);
        }

        if (this.collideGround(state, ball, true)) {
            this.engine.fireEvent(EVENT_INGAME_BALL_HITS_GROUND);
        }

        ball.motion.set(this.move.sub(ball.position).scale(-1));
        ball.ancle += ball.motion.x * 4;
    }

    /**
     *
     */
    updateCamera(state) {
        // Calculate new camera position
        state.camera.x = -state.ball.position.x * state.cameraScrollFactor;
    }

    /**
     * @param ball
     */
    collideBallNet(ball) {
        // Init
        var collided = false;

        // Collide ball with net
        if (ball.position.y >= this.netTip.position.y) {
            collided = this.collideWall(ball, this.netTip.radius, OUTSIDE, true);
        }

        // Collide ball with net tip
        if (this.collideEntities(ball, this.netTip)) {
            collided = true;
        }

        // Return result
        return collided;
    }

    /**
     * @param {*} dynamicEntity
     * @param {*} staticEntity
     * @returns
     */
    collideEntities(dynamicEntity, staticEntity) {
        // Init
        this.posD.set(dynamicEntity.position).add(dynamicEntity.center);
        this.posS.set(staticEntity.position).add(staticEntity.center);
        this.diff.set(this.posD).sub(this.posS);
        var length = this.diff.length();
        var radius = dynamicEntity.radius + staticEntity.radius;

        // Check for collision
        if (length < radius && length > 0) {
            // Normalize diff
            this.diff.scale(1 / length);

            // Move dynamic entitiy to "save" non-colliding position
            dynamicEntity.position
                .set(this.diff)
                .scale(radius * 1.01)
                .add(this.posS)
                .sub(dynamicEntity.center);

            // Calculate force from dynamic entity
            mirrorPointOnLine(ZERO, this.diff, dynamicEntity.velocity, this.forceD);
            this.forceD.invert();

            // Prepare result
            this.result.zero();

            // Apply force according to ancle between diff and force
            this.result.add(this.forceD.scale(this.clamp(this.diff.dot(this.forceD))));

            // Calculate force from static entity
            mirrorPointOnLine(ZERO, this.diff, staticEntity.velocity, this.forceS);

            // Apply force according to ancle between diff and force
            this.result.add(this.forceS.scale(this.clamp(this.diff.dot(this.forceS))));

            // Apply velocity if result vector is not null
            if (!this.result.isZero()) {
                dynamicEntity.velocity.set(this.result);
            }

            return true;
        }

        return false;
    }

    /**
     * @param {*} state
     * @param {*} entity
     * @param {*} bounce
     * @returns
     */
    collideGround(state, entity, bounce) {
        // Init
        var collided = false;
        var shift = entity.radius + entity.center.y;
        var ground = state.ground - (shift < 0 ? 0 : shift);

        // Test ground collision
        if (entity.position.y > ground) {
            if (bounce) {
                // Bounce off the ground
                entity.position.y -= 1.01 * (entity.position.y - ground);
                entity.velocity.y = -entity.velocity.y * ((entity.elasticity + state.groundElasticity) / 2);
            } else {
                // Do not bounce
                entity.position.y = ground;
                entity.velocity.y = 0;
            }

            collided = true;
        }

        // Apply friction
        if (entity.position.y > ground - 0.1) {
            entity.velocity.x *= entity.friction;
            collided = true;
        }

        // Return result
        return collided;
    }

    /**
     * @param entity
     */
    collideWall(entity, wall, type, bounce) {
        // Init
        var isin = type == INSIDE;
        var abspos = Math.abs(entity.position.x);
        var inset = wall + (isin ? -1 : 1) * entity.radius;

        // Check wall collision
        if (isin ? abspos > inset : abspos < inset) {
            entity.position.x = entity.position.x > 0 ? inset : -inset;
            entity.velocity.x = bounce ? -entity.velocity.x * entity.elasticity : 0;
            return true;
        }

        return false;
    }

    /**
     * @param entity
     */
    moveEntity(state, entity) {
        // Add gravity
        entity.velocity.y += state.gravity;

        // Apply maximum speed
        var speed = entity.velocity.length();
        if (entity.maxSpeed > 0 && speed > entity.maxSpeed) {
            entity.velocity.scale(entity.maxSpeed / speed);
        }

        // Move entity
        entity.position.add(entity.velocity);
    }

    /**
     * @param value
     * @return
     */
    clamp(value) {
        return value > 1 ? 1 : value < -1 ? -1 : value;
    }
}
